import { getInitials as getInitialsHelper } from "@/utils/helpers"

import { Invitation } from "."
import { Base } from "./Base"
import { Project } from "./Project"
import { DeveloperRoles, Picture, User, UserWithRole } from "./User"

export enum TeamRolesWithAccess {
  DEVELOPER_TEAM_MANAGERS = "DEVELOPER_TEAM_MANAGERS",
  DEVOPS_TEAM_MANAGERS = "DEVOPS_TEAM_MANAGERS",
  DEVELOPER_PM_AND_SCRUM_MASTERS = "DEVELOPER_PM_AND_SCRUM_MASTERS",
  PRODUCT_OWNERS_AND_PRODUCT_MANAGERS = "PRODUCT_OWNERS_AND_PRODUCT_MANAGERS",

  MARKETING_TEAM_MANAGERS = "MARKETING_TEAM_MANAGERS",
  MARKETING_PM = "MARKETING_PM",

  ENGINEERING_MANAGERS = "ENGINEERING_MANAGERS",
  PRODUCT_OWNERS_AND_PM = "PRODUCT_OWNERS_AND_PM",
  SCRUM_MASTER = "SCRUM_MASTER",

  UI_UX_PRODUCT_OWNER_AND_PRODUCT_MANAGER = "UI_UX_PRODUCT_OWNER_AND_PRODUCT_MANAGER",
  UI_UX_MANGERS = "UI_UX_MANGERS",
  UI_UX_PM = "UI_UX_PM"
}

export type TeamType = "DEVELOPMENT" | "UI_UX" | "DEVOPS" | "MARKETING" | "NONE"

export const teamType: TeamType[] = ["DEVELOPMENT", "UI_UX", "DEVOPS", "MARKETING"]

export interface RoleStats {
  type: string
  count: number
}

export interface TeamTypeOption {
  label: string
  id: TeamType
  icon: string
}

export interface GetAvailableTeamTypesParams {
  teams: Team[]
  filterByProject?: string
}

export class TeamProfile extends Base<TeamProfile> {
  name = ""
  description = ""
  enabled = true

  constructor(props?: TeamProfile) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  isModified(keys?: (keyof TeamProfile)[], inverse?: boolean) {
    let isModified = false
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }
}

export interface TeamProps {
  name: string
  id: string
  users: User[]
  picture: Picture
  projects: Project[]
  description?: string
  enabled: boolean
  size?: number
  projectsCount?: number
  usersAndRoles?: UserWithRole[]
  rolesAllowedToCreateItem?: { [key in TeamRolesWithAccess]?: boolean }
  rolesOfAccessToItemCreation?: TeamRolesWithAccess[]
  usersToInvite?: Invitation[]
  projectIds?: string[]
  urlName?: string
  demo: boolean
  includedInSubscription?: boolean
  hasScrumSprint?: boolean
  type?: TeamType
  roleStats?: RoleStats[]
  disableSprintCreation?: boolean
  isVirtual?: boolean
}

export interface TeamPreview {
  name: string
  id: string
  picture: Picture
  users?: User[]
  accountId: string
  enabled: boolean
  size?: number
  projectsCount?: number
  urlName?: string
  team: TeamType
  projectIds?: string[]
}

export interface TeamDTO {
  name: string
  id: string
  users: User[]
  picture: Picture
  projects: Project[]
  description?: string
  enabled: boolean
  size?: number
  projectsCount?: number
  urlName: string
  demo: boolean
  includedInSubscription?: boolean
  hasScrumSprint?: boolean
  type?: TeamType
  disableSprintCreation?: boolean
  isVirtual?: boolean
}

export class Team {
  name: string
  id: string
  users: User[]
  picture: Picture
  projects: Project[]
  description?: string
  enabled: boolean
  size?: number
  projectsCount?: number
  usersAndRoles?: UserWithRole[]
  rolesAllowedToCreateItem: { [key in TeamRolesWithAccess]?: boolean }
  rolesOfAccessToItemCreation: TeamRolesWithAccess[]
  usersToInvite: Invitation[]
  projectIds: string[]
  urlName: string
  demo: boolean
  hideAvatar?: boolean
  includedInSubscription?: boolean
  hasScrumSprint?: boolean
  type?: TeamType
  roleStats: RoleStats[]
  disableSprintCreation?: boolean
  isVirtual?: boolean

  static getRolesByTeamType(teamType: TeamType): TeamRolesWithAccess[] {
    const marketingRoles: TeamRolesWithAccess[] = [
      TeamRolesWithAccess.MARKETING_TEAM_MANAGERS,
      TeamRolesWithAccess.PRODUCT_OWNERS_AND_PRODUCT_MANAGERS,
      TeamRolesWithAccess.MARKETING_PM
    ]

    const developmentRoles: TeamRolesWithAccess[] = [
      TeamRolesWithAccess.DEVELOPER_TEAM_MANAGERS,
      TeamRolesWithAccess.PRODUCT_OWNERS_AND_PRODUCT_MANAGERS,
      TeamRolesWithAccess.DEVELOPER_PM_AND_SCRUM_MASTERS
    ]

    const devopsRoles: TeamRolesWithAccess[] = [
      TeamRolesWithAccess.DEVOPS_TEAM_MANAGERS,
      TeamRolesWithAccess.PRODUCT_OWNERS_AND_PRODUCT_MANAGERS,
      TeamRolesWithAccess.DEVELOPER_PM_AND_SCRUM_MASTERS
    ]

    const uiUxRoles: TeamRolesWithAccess[] = [
      TeamRolesWithAccess.UI_UX_MANGERS,
      TeamRolesWithAccess.UI_UX_PRODUCT_OWNER_AND_PRODUCT_MANAGER,
      TeamRolesWithAccess.UI_UX_PM
    ]

    if (teamType === "MARKETING") return marketingRoles
    if (teamType === "DEVOPS") return devopsRoles
    if (teamType === "UI_UX") return uiUxRoles
    return developmentRoles
  }

  constructor(props?: TeamProps) {
    this.setDefaultData()

    if (!props) {
      return
    }

    const {
      name,
      id,
      users,
      picture,
      projects,
      description,
      enabled,
      size,
      projectsCount,
      usersAndRoles,
      rolesAllowedToCreateItem,
      rolesOfAccessToItemCreation,
      usersToInvite,
      projectIds,
      urlName,
      demo,
      includedInSubscription,
      hasScrumSprint,
      type,
      roleStats,
      disableSprintCreation,
      isVirtual
    } = props

    this.name = name ?? ""
    this.id = id ?? ""
    this.users = users?.map(user => User.create(user)) ?? []
    this.picture = picture ?? { pictureUrl: null }
    this.projects = projects ?? []
    this.description = description ?? ""
    this.enabled = enabled ?? true
    this.size = size ?? 0
    this.projectsCount = projectsCount ?? 0
    this.usersAndRoles = usersAndRoles ?? []
    this.rolesAllowedToCreateItem = rolesAllowedToCreateItem ?? {}
    this.rolesOfAccessToItemCreation = rolesOfAccessToItemCreation ?? []
    this.usersToInvite = usersToInvite ?? []
    this.projectIds = projectIds ?? []
    this.urlName = urlName ?? ""
    this.demo = demo ?? false
    this.hideAvatar = false
    this.includedInSubscription = includedInSubscription ?? true
    this.hasScrumSprint = hasScrumSprint ?? true
    this.type = type ?? "DEVELOPMENT"
    this.roleStats = roleStats ?? []
    this.disableSprintCreation = disableSprintCreation ?? false
    this.isVirtual = isVirtual ?? false
  }

  getInitials(): string {
    return getInitialsHelper(this.name)
  }

  setDefaultData() {
    this.name = ""
    this.id = ""
    this.users = []
    this.projects = []
    this.description = ""
    this.enabled = true
    this.size = 0
    this.projectsCount = 0
    this.usersAndRoles = []
    this.rolesAllowedToCreateItem = {}
    this.rolesOfAccessToItemCreation = []
    this.usersToInvite = []
    this.projectIds = []
    this.urlName = ""
    this.demo = false
    this.projects = []
    this.hasScrumSprint = true
    this.type = "DEVELOPMENT"
    this.disableSprintCreation = false
    this.isVirtual = false
  }
  static create(teamDTO: TeamDTO): Team {
    if (teamDTO) {
      return new Team(teamDTO)
    }
    return new Team()
  }

  static createDefault(): Team {
    return new Team()
  }

  get usersDevOps() {
    return this.users.filter(u => u.teamRole === "DEVOPS")
  }

  get usersEngineer() {
    return this.users.filter(u => u.teamRole === "ENGINEER" || u.teamRole === "DEV_ENGINEER")
  }

  get usersUi_ux() {
    return this.users.filter(u => u.teamRole === "UI_UX")
  }

  get usersQa() {
    return this.users.filter(u => u.teamRole === "QA")
  }

  get usersWithDeveloperRole() {
    return this.users.filter(u => DeveloperRoles[u.teamRole])
  }

  get userCountByRoleStats() {
    return this.roleStats.reduce((sum, roleStat) => sum + roleStat.count, 0)
  }

  getJsonObj() {
    return {
      name: this.name.trim(),
      id: this.id,
      users: this.users,
      picture: this.picture,
      projects: this.projects,
      description: this.description,
      enabled: this.enabled,
      size: this.size,
      projectsCount: this.projectsCount,
      usersAndRoles: this.usersAndRoles,
      rolesAllowedToCreateItem: this.rolesAllowedToCreateItem,
      usersToInvite: this.usersToInvite,
      projectIds: this.projects.map(project => project.id),
      urlName: this.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-"),
      hasScrumSprint: this.hasScrumSprint,
      type: this.type
    }
  }

  getUsersByRoleType(roleType: string) {
    return this.users.filter(user => user.roleType === roleType)
  }

  static checkDeletedFromProject(team: Team, projectId?: string): boolean {
    return (
      !team.isVirtual && !!projectId && !!team.projectIds && !team.projectIds.includes(projectId)
    )
  }

  static getAvailableTeamTypes({
    teams,
    filterByProject
  }: GetAvailableTeamTypesParams): TeamType[] {
    return teams
      .filter(team => (filterByProject ? team.projectIds?.includes(filterByProject) : true))
      .map(team => team.type)
      .filter((value, index, self): value is TeamType => !!value && self.indexOf(value) === index)
  }
}
